import { createContext, useContext, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AppFeatureValue, AppActionValue, permissionFor } from '@/types/auth.types';
import { useAuthStore } from '@/store/auth.store';
import { apiClient } from '@/lib/api/client';

interface PermissionsContextType {
  permissions: Set<string>;
  features: Set<string>;
  isLoading: boolean;
  hasPermission: (feature: AppFeatureValue, action: AppActionValue) => boolean;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

async function fetchPermissions(): Promise<string[]> {
  const res = await apiClient.get<string[]>('/auth/permissions/me');
  // Sentinel returns a plain string[] — no envelope
  if (Array.isArray(res.data)) return res.data;
  return [];
}

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const { data, isLoading } = useQuery({
    queryKey: ['permissions', 'me'],
    queryFn: fetchPermissions,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
  });

  const permissions = new Set<string>(data ?? []);
  const features = new Set<string>(
    (data ?? []).map((p) => p.split('.')[1]).filter((f): f is string => Boolean(f))
  );

  const hasPermission = (feature: AppFeatureValue, action: AppActionValue): boolean => {
    return permissions.has(permissionFor(feature, action));
  };

  return (
    <PermissionsContext.Provider value={{ permissions, features, isLoading, hasPermission }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions(): Set<string> {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within PermissionsProvider');
  }
  return context.permissions;
}

export function useHasPermission() {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('useHasPermission must be used within PermissionsProvider');
  }
  return context.hasPermission;
}

export function usePermissionsLoading(): boolean {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissionsLoading must be used within PermissionsProvider');
  }
  return context.isLoading;
}
